import { type CardTone } from 'partner360/components/card'
import { type TileConfig } from 'partner360/components/tileBoard'
import { MANAGER_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/managerCockpitTourTargets'

import {
  CockpitAttentionStatusesCollapsed,
  CockpitAttentionStatusesExpanded,
  CockpitAttentionStatusesTitle,
} from '../../cockpitAttentionStatuses'
import { CockpitDocumentsAndLinksCollapsed } from '../../cockpitDocumentsAndLinks/CockpitDocumentsAndLinksCollapsed'
import { CockpitDocumentsAndLinksExpanded } from '../../cockpitDocumentsAndLinks/CockpitDocumentsAndLinksExpanded'
import { CockpitDocumentsAndLinksTitle } from '../../cockpitDocumentsAndLinks/components/cockpitDocumentsAndLinksTitle/CockpitDocumentsAndLinksTitle'
import type { TDocumentType } from '../../cockpitDocumentsAndLinks/types'
import { CockpitKeyInitiativesCollapsed } from '../../cockpitKeyInitiatives/CockpitKeyInitiativesCollapsed'
import { CockpitKeyInitiativesExpanded } from '../../cockpitKeyInitiatives/CockpitKeyInitiativesExpanded'
import { CockpitKeyInitiativesTitle } from '../../cockpitKeyInitiatives/CockpitKeyInitiativesTitle'
import { CockpitRevenueAndSobCollapsed } from '../../cockpitRevenueAndSob/CockpitRevenueAndSobCollapsed'
import { CockpitRevenueAndSobExpanded } from '../../cockpitRevenueAndSob/CockpitRevenueAndSobExpanded'
import { CockpitRevenueAndSobTitle } from '../../cockpitRevenueAndSob/components'

// First row (Financial & SOB, Initiatives) takes exactly its content height at any viewport and
// never gets shorter; the attention/documents rows below share whatever height is left, but never
// less than MANAGER_COCKPIT_LOWER_TILE_MIN_HEIGHT. On a short viewport the page scrolls instead.
export const MANAGER_COCKPIT_ROW_SIZES = ['auto']

const MANAGER_COCKPIT_LOWER_TILE_MIN_HEIGHT = 320

/** Named so a tour step's EXPAND_TILE payload cannot drift from the board. */
export const MANAGER_COCKPIT_TILE_IDS = {
  revenueAndSob: 'revenueAndSob',
  initiatives: 'initiatives',
  attention: 'tile3x2',
  documentsAndLinks: 'documentsAndLinks',
} as const

interface IDocumentsNavigationOptions {
  documentsType?: TDocumentType
  onDocumentsTypeChange?: (type: TDocumentType) => void
}

export const buildTiles = (
  attentionTone?: CardTone,
  { documentsType, onDocumentsTypeChange }: IDocumentsNavigationOptions = {}
): TileConfig[] => [
  {
    id: MANAGER_COCKPIT_TILE_IDS.revenueAndSob,
    tourId: TOUR_TARGETS.tileRevenue,
    expandedTourId: TOUR_TARGETS.tileRevenueExpanded,
    title: 'Financial & SOB',
    subtitle: '',
    colSpan: 2,
    rowSpan: 1,
    minHeight: 'min-content',
    hideExpandButton: true,
    renderTitle: ({ title, expanded, onToggle }) => (
      <CockpitRevenueAndSobTitle
        title={title}
        expanded={expanded}
        onToggle={onToggle}
        withQuota
        isManagerCockpitMode
        viewSwitchTourId={TOUR_TARGETS.revenueViewSwitch}
      />
    ),
    renderCollapsed: ({ expand }) => <CockpitRevenueAndSobCollapsed onExpand={expand} />,
    renderExpanded: () => <CockpitRevenueAndSobExpanded isManagerCockpitMode />,
  },
  {
    id: MANAGER_COCKPIT_TILE_IDS.initiatives,
    tourId: TOUR_TARGETS.tileInitiatives,
    expandedTourId: TOUR_TARGETS.tileInitiativesExpanded,
    title: 'Initiatives',
    subtitle: '',
    colSpan: 2,
    rowSpan: 1,
    minHeight: 'min-content',
    hideExpandButton: true,
    renderTitle: ({ title, expanded, onToggle }) => (
      <CockpitKeyInitiativesTitle
        title={title}
        expanded={expanded}
        onToggle={onToggle}
        isManagerCockpitMode
        viewSwitchTourId={TOUR_TARGETS.initiativesViewSwitch}
      />
    ),
    renderCollapsed: ({ expand }) => (
      <CockpitKeyInitiativesCollapsed onExpand={expand} isManagerCockpitMode />
    ),
    renderExpanded: () => <CockpitKeyInitiativesExpanded isManagerCockpitMode showGeoHierarchy />,
  },
  {
    id: MANAGER_COCKPIT_TILE_IDS.attention,
    tourId: TOUR_TARGETS.tileAttention,
    expandedTourId: TOUR_TARGETS.tileAttentionExpanded,
    title: 'What needs attention',
    subtitle: '',
    tone: attentionTone,
    colSpan: 3,
    rowSpan: 2,
    minHeight: MANAGER_COCKPIT_LOWER_TILE_MIN_HEIGHT,
    hideExpandButton: true,
    renderTitle: ({ title, expanded, onToggle }) => (
      <CockpitAttentionStatusesTitle
        title={title}
        expanded={expanded}
        onToggle={onToggle}
        isManagerCockpitMode
      />
    ),
    renderCollapsed: ({ expand }) => (
      <CockpitAttentionStatusesCollapsed
        onExpand={expand}
        title="What needs attention"
        isManagerCockpitMode
      />
    ),
    renderExpanded: ({ collapse }) => (
      <CockpitAttentionStatusesExpanded onCollapse={collapse} isManagerCockpitMode />
    ),
  },
  {
    id: MANAGER_COCKPIT_TILE_IDS.documentsAndLinks,
    tourId: TOUR_TARGETS.tileDocuments,
    expandedTourId: TOUR_TARGETS.tileDocumentsExpanded,
    title: 'Documents and links',
    subtitle: '',
    colSpan: 1,
    rowSpan: 2,
    minHeight: MANAGER_COCKPIT_LOWER_TILE_MIN_HEIGHT,
    hideExpandButton: true,
    renderTitle: ({ title, expanded, onToggle }) => (
      <CockpitDocumentsAndLinksTitle
        title={title}
        expanded={expanded}
        onToggle={onToggle}
        isManagerCockpitMode
      />
    ),
    renderCollapsed: ({ expand }) => (
      <CockpitDocumentsAndLinksCollapsed
        onExpand={expand}
        isManagerCockpitMode
        onViewDocuments={(type) => {
          onDocumentsTypeChange?.(type)
          expand()
        }}
      />
    ),
    renderExpanded: () => (
      <CockpitDocumentsAndLinksExpanded isManagerCockpitMode initialDocumentType={documentsType} />
    ),
  },
]
